package com.wintermar.inspection.feature.presentation.inspection.complete_answer

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.TextBodyXLarge
import com.wintermar.inspection.core.ui.component.TextTitleS
import com.wintermar.inspection.core.ui.component.button.Button
import com.wintermar.inspection.core.ui.component.button.variant.ButtonVariant
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.feature.domain.model.answer.CompleteAnswerModel
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.all_done_your_inspection_has_been_successfully_submitted
import inspection.composeapp.generated.resources.complete
import inspection.composeapp.generated.resources.done
import inspection.composeapp.generated.resources.files
import inspection.composeapp.generated.resources.findings
import inspection.composeapp.generated.resources.ic_check
import inspection.composeapp.generated.resources.inspection_completed
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
fun CompleteAnswerScreen(
    summaryData: CompleteAnswerModel,
    dimension: AppDimension = LocalAppTheme.current.dimension,
    onDone: () -> Unit
) {
    val density = LocalDensity.current
    val screenHeightDp = with(density) {
        LocalWindowInfo.current.containerSize.height.toDp()
    }

    val screenWidthDp = with(density) {
        LocalWindowInfo.current.containerSize.width.toDp()
    }

    val fillWidth by remember {
        var value = 1f
        setupContent { value = it }
        mutableStateOf(value)
    }
    val isMonitorScreen = fillWidth != 1f

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(
                        Color(0xFF3395FF),
                        Color(0xFF0F5299)
                    )
                )
            ),
        contentAlignment = Alignment.TopCenter
    ) {
        if (!isMonitorScreen) Ellipse(
            modifier = Modifier.fillMaxWidth(),
            width = screenWidthDp,
            height = screenHeightDp * 0.4f
        )

        Column(
            modifier = Modifier
                .fillMaxWidth(fillWidth)
                .fillMaxHeight()
                .padding(horizontal = dimension.spacing.space4),
            verticalArrangement = if (!isMonitorScreen) Arrangement.SpaceBetween else Arrangement.Top,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Row(
                modifier = Modifier
                    .padding(top = dimension.spacing.space11)
                    .size(dimension.spacing.space12)
                    .shadow(elevation = dimension.spacing.space1, shape = CircleShape)
                    .clip(shape = CircleShape)
                    .background(
                        color = Color(0xFF3EA7B6)
                    )
                    .border(
                        width = 2.dp,
                        color = Color(0xFF5AB9AB),
                        shape = CircleShape
                    )
                    .padding(dimension.spacing.space4),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_check),
                    modifier = Modifier.size(dimension.size.icon.xxl),
                    tint = Color.White,
                    contentDescription = stringResource(Res.string.complete)
                )
            }
            if (isMonitorScreen) SpacerContent()
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(
                    space = dimension.spacing.space4,
                    alignment = Alignment.CenterVertically
                )
            ) {
                TextTitleS(
                    text = stringResource(
                        Res.string.inspection_completed,
                        summaryData.templateName
                    ),
                    color = { it.inverse },
                    bold = true,
                    textAlign = TextAlign.Center
                )

                TextBodySmall(
                    text = stringResource(Res.string.all_done_your_inspection_has_been_successfully_submitted),
                    color = { it.inverse },
                    textAlign = TextAlign.Center
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = dimension.spacing.space6)
                        .padding(horizontal = dimension.spacing.space5),
                    horizontalArrangement = Arrangement.spacedBy(
                        space = dimension.spacing.space2,
                        alignment = Alignment.CenterHorizontally
                    ),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TotalContent(
                        title = stringResource(Res.string.findings),
                        value = summaryData.totalFindings,
                        modifier = Modifier.weight(1f)
                    )

                    TotalContent(
                        title = stringResource(Res.string.files),
                        value = summaryData.totalFiles,
                        modifier = Modifier.weight(1f)
                    )
                }
            }
            if (isMonitorScreen) SpacerContent()
            Row(
                modifier = Modifier
                    .padding(bottom = dimension.spacing.space9)
                    .then(
                        if (isMonitorScreen) Modifier.padding(horizontal = dimension.spacing.space5)
                        else Modifier
                    )
            ) {
                Button(
                    buttonVariant = ButtonVariant.LargeSecondary,
                    text = stringResource(Res.string.done),
                    modifier = Modifier.fillMaxWidth(),
                    onClick = onDone
                )
            }
        }
    }
}

@Composable
private fun Ellipse(modifier: Modifier = Modifier, width: Dp = 375.dp, height: Dp = 349.dp) {
    Box(modifier = modifier.size(width, height)) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            val vpW = 375f
            val vpH = 349f
            val scaleX = size.width / vpW
            val scaleY = size.height / vpH
            val colors = listOf(Color(0xFF2983E4), Color(0xFF1D68B9))
            val topLeft = Offset(-131f * scaleX, -288f * scaleY)
            val ovalSize = Size(637f * scaleX, 637f * scaleY)

            val verticalStartY = topLeft.y
            val verticalEndY = topLeft.y + ovalSize.height

            val verticalBrush = Brush.verticalGradient(
                colors = colors,
                startY = verticalStartY,
                endY = verticalEndY
            )

            drawOval(
                brush = verticalBrush,
                topLeft = topLeft,
                size = ovalSize,
                alpha = 0.5f
            )
        }
    }
}

@Composable
private fun TotalContent(
    title: String,
    value: Int,
    modifier: Modifier = Modifier,
    dimension: AppDimension = LocalAppTheme.current.dimension
) {
    Column(
        modifier = modifier
            .background(
                color = Color(0xFF2983E4).copy(alpha = 0.5f),
                shape = RoundedCornerShape(dimension.radius.md)
            )
            .border(
                width = 1.dp,
                color = Color.White.copy(alpha = 0.1f),
                shape = RoundedCornerShape(dimension.radius.md)
            )
            .padding(dimension.spacing.space2),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(
            space = dimension.spacing.space1,
            alignment = Alignment.CenterVertically
        )
    ) {
        TextBodyXLarge(
            text = "$value",
            bold = true,
            textAlign = TextAlign.Center,
            color = { it.inverse }
        )

        TextBodySmall(
            text = title,
            textAlign = TextAlign.Center,
            color = { it.inverse }
        )
    }
}

@Composable
private fun SpacerContent(
    dimension: AppDimension = LocalAppTheme.current.dimension
) {
    Spacer(modifier = Modifier.height(dimension.spacing.space10))
}

expect fun setupContent(
    onLoad: (fillWidth: Float) -> Unit
)
